import React, { forwardRef, RefObject } from 'react';
import SmallDonutChartDetail, {
  ISpaceChartData,
} from 'spw-components/charts/small-donut-chart-detail';
import { ISpaceTypesUSF } from 'spw-programming-projects/program-summary/program-summary-tabs/program-scenario-summary';
import { IProjectSpaceTypeCategory } from 'spw-redux/modules/projects/types';
import { useSelector } from 'react-redux';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { projectColors as selectProjectColors } from 'spw-redux/modules/projects/selectors';
import { getColor } from 'spw-shared/utils';
import { PlainWorksheet } from '../../../../models/Worksheet';

interface ISpaceTypeAllocationUsfDetailChart {
  worksheet?: PlainWorksheet;
  isTotalProgramScenario: boolean;
  projectSpaceTypeCategories: IProjectSpaceTypeCategory[];
  spaceTypesUsf: ISpaceTypesUSF;
  style?: { [key: string]: number | string };
  showNamesInTable?: boolean;
  totalProgramWorksheets?: PlainWorksheet[];
}

const SpaceTypeAllocationUsfDetailChart = forwardRef<
  HTMLElement,
  ISpaceTypeAllocationUsfDetailChart
>(
  (
    {
      worksheet,
      isTotalProgramScenario,
      projectSpaceTypeCategories,
      spaceTypesUsf,
      style,
      showNamesInTable,
      totalProgramWorksheets,
    },
    ref
  ) => {
    const { convertArea, getUnit } = useUnitConversion();
    const data: [string, string | number][] = [['Space Type', 'Count']];
    const projectColors = useSelector(selectProjectColors);

    projectSpaceTypeCategories.forEach(
      (projectSpaceTypeCategory: IProjectSpaceTypeCategory) => {
        const { category } = projectSpaceTypeCategory.defaultSpaceTypeCategory;
        data.push([
          projectSpaceTypeCategory.name,
          spaceTypesUsf[category as keyof ISpaceTypesUSF] || 0,
        ]);
      }
    );

    const spaceTypesData: ISpaceChartData[] = projectSpaceTypeCategories.map(
      (projectSpaceTypeCategory: IProjectSpaceTypeCategory) => {
        const { category } = projectSpaceTypeCategory.defaultSpaceTypeCategory;
        const spaceTypeUsf = spaceTypesUsf[category as keyof ISpaceTypesUSF];
        return {
          quantity: convertArea(spaceTypeUsf),
          percent: spaceTypeUsf / worksheet.subtotalProgramUSF,
          color: getColor(
            projectSpaceTypeCategory.defaultSpaceTypeCategory.category,
            projectColors
          ),
          name: projectSpaceTypeCategory.name,
          category,
        };
      }
    );

    return (
      <div ref={ref as RefObject<HTMLDivElement>} style={{ height: '100%' }}>
        <SmallDonutChartDetail
          style={style || { minHeight: 232, height: '100%' }}
          title="SPACE TYPE ALLOCATION"
          subtitle={`Subtotal Program ${getUnit('area')}`}
          total={convertArea(worksheet.subtotalProgramUSF)}
          spacesData={spaceTypesData}
          data={data}
          colors={spaceTypesData.map((spaceTypeData) => spaceTypeData.color)}
          showNamesInTable={showNamesInTable}
          isTotalProgramScenario={isTotalProgramScenario}
          totalProgramWorksheets={totalProgramWorksheets}
          showStackedNames
          testingPrefix="programming_ps_spacetypealloc_detailed"
          includeDisclaimer
          anchorRef={ref}
          isDetailedChart
        />
      </div>
    );
  }
);

export default React.memo(SpaceTypeAllocationUsfDetailChart);
